/**
 * The register's slicer axes (`KLQ-20260820-005` §6).
 *
 * **Which axes exist is read, not authored.** `GET /surfaces/project-tasks/descriptor` says
 * what the endpoint filters on and what values each field takes, and that is where the rail's
 * dropdown comes from. Nothing in this file lists a status, a kind or a priority — add one to
 * the enum on the server and it is in the rail on the next deploy, with no change here.
 *
 * **What this file does own is the binding**, and it owns it because it is genuinely this
 * screen's business rather than the descriptor's:
 *
 * 1. **Which of this screen's filter fields a wire field is.** `TaskFilters` is a typed object
 *    with its own names — `assignee` for `assignee_user_id`, `labels` for `label_ids` — because
 *    it is also the URL's shape and a person types those (`filters.ts`). A server field this
 *    screen does not bind is simply not offered, which is the same rule one step further in:
 *    an axis the *client* cannot apply must not be drawn either.
 * 2. **Where a `values_from` axis's choices actually come from.** The descriptor names a path;
 *    turning that path into rows means knowing which field is the id and which is the label,
 *    and those differ per endpoint. The screen's own typed queries answer it — one more request
 *    is not made for an axis nobody opened.
 * 3. **What a person sees it called.** A field name is not a label. `t()` with the wire field
 *    as the key, falling back to the field itself, so a new axis is legible before anybody
 *    writes copy for it and obviously unfinished until they do.
 *
 * **The rail writes one axis and touches no other.** Axes compose the way the wire composes:
 * OR within an axis, AND across them (`MCK-20260812-006`'s repeated-parameter rule). Selecting
 * two statuses is one navigation; the status axis and the location axis narrow together and
 * each is cleared on its own.
 */

import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import type { operations } from "@/api/generated/openapi";

import type { SlicerFacet, SlicerOption } from "@/components";
import { hierarchyQueryOptions, slicerOptions } from "@/features/drawings";
import { membersQueryOptions } from "@/features/people";
import { offerableAxes, useSurface } from "@/lib/surfaces";
import type { SurfaceField } from "@/lib/surfaces";
import { useProjectObjects } from "@/features/projects";
import { useLooseMessages } from "@/lib/i18n";

import { projectLabelsQueryOptions } from "./queries";
import type { TaskFilters } from "./filters";
import type { TaskKind, TaskPriority, TaskStatus } from "./api";

/**
 * A wire name as this app's catalogs spell it: `assignee_user_id` → `assigneeUserId`.
 *
 * A function rather than a table, so a field nobody has met yet still resolves to a key. It is
 * the whole of the naming convention between the two sides — the server speaks snake_case
 * because that is what a query string reads like by hand, and the catalogs speak camelCase
 * because every other key in them does.
 */
function camel(wire: string): string {
  return wire.replace(/_(.)/g, (_all, letter: string) => letter.toUpperCase());
}

/**
 * Every query parameter `GET /projects/{project_id}/tasks` accepts, as the generated client
 * spells it. `BINDINGS` is keyed on this rather than on `string`, so a binding for a parameter
 * the endpoint does not take is a compile error — the structural half of `KLQ-20260820-005`'s
 * *"no request carries a parameter introduced by the panel"*.
 */
type TaskQueryParameter = keyof NonNullable<
  operations["list_tasks"]["parameters"]["query"]
>;

/** How one wire field is read out of this screen's filter state and written back into it. */
interface Binding {
  readonly read: (filters: TaskFilters) => readonly string[];
  readonly write: (
    filters: TaskFilters,
    values: readonly string[],
  ) => TaskFilters;
}

/**
 * The six fields this screen binds, by their **wire** name.
 *
 * Deliberately keyed by what the server calls them, so the descriptor is the thing being looked
 * up rather than something being matched against. A seventh server filter needs one entry here
 * — that is the cost of `TaskFilters` being a typed, total object that is also the URL's shape,
 * and it is a cost worth paying: an untyped bag would make every reader of a filter guess.
 *
 * The casts are the one place a wire string re-enters the typed world. They are safe because
 * the values came from this endpoint's own enum in its own descriptor — the same declaration
 * `TaskStatus` is generated from — and unsafe only if the server and the generated client have
 * drifted, which CI's contract diff is what catches.
 */
const BINDINGS = {
  under: {
    read: (filters) => filters.under,
    write: (filters, values) => ({ ...filters, under: [...values] }),
  },
  kind: {
    read: (filters) => filters.kind,
    write: (filters, values) => ({
      ...filters,
      kind: values as readonly TaskKind[],
    }),
  },
  status: {
    read: (filters) => filters.status,
    write: (filters, values) => ({
      ...filters,
      status: values as readonly TaskStatus[],
    }),
  },
  priority: {
    read: (filters) => filters.priority,
    write: (filters, values) => ({
      ...filters,
      priority: values as readonly TaskPriority[],
    }),
  },
  assignee_user_id: {
    read: (filters) => filters.assignee,
    write: (filters, values) => ({ ...filters, assignee: [...values] }),
  },
  label_ids: {
    read: (filters) => filters.labels,
    write: (filters, values) => ({ ...filters, labels: [...values] }),
  },
  object_id: {
    read: (filters) => filters.objects,
    write: (filters, values) => ({ ...filters, objects: [...values] }),
  },
} as const satisfies Partial<Record<TaskQueryParameter, Binding>>;

type BoundField = keyof typeof BINDINGS;

/** Whether this screen binds `field` — own keys only, so `toString` is not an axis. */
function isBound(field: string): field is BoundField {
  return Object.hasOwn(BINDINGS, field);
}

/**
 * What choosing `values` on the axis `facetId` does to the filter state, with every other axis
 * left as it was. Pure, so the rail's one write is exercisable without a rendered hook; an axis
 * this screen does not bind leaves the state untouched.
 */
export function applyTaskAxis(
  filters: TaskFilters,
  facetId: string,
  values: readonly string[],
): TaskFilters {
  return isBound(facetId) ? BINDINGS[facetId].write(filters, values) : filters;
}

export interface TaskAxes {
  readonly facets: readonly SlicerFacet[];
  /** What is currently chosen on `facetId`, for the rail's `sliced`. */
  readonly sliced: (facetId: string) => readonly string[];
  /** The whole next filter state, with only `facetId` changed. */
  readonly apply: (
    filters: TaskFilters,
    facetId: string,
    values: readonly string[],
  ) => TaskFilters;
}

/**
 * The rail's facets for this screen, and the two functions that move one axis.
 *
 * The three value sources are fetched unconditionally rather than per open axis, and that is
 * not an oversight: **all three are already on this screen.** The hierarchy is the drawings
 * tab's cached tree, the members list is what the register's assignee column reads, and the
 * labels are its chips. Asking for them here costs nothing because nothing here is a new
 * request — which is exactly why the rail is worth having on this screen and would not be on
 * one that had none of them.
 */
export function useTaskAxes(projectId: string, filters: TaskFilters): TaskAxes {
  // Loose, because the key set follows the descriptor rather than this bundle: an axis the
  // server grows before anybody writes copy for it appears named by its own field.
  const t = useLooseMessages("tasks");
  const descriptor = useSurface("project-tasks");
  const hierarchy = useQuery(hierarchyQueryOptions(projectId));
  const members = useQuery(membersQueryOptions(projectId));
  const labels = useQuery(projectLabelsQueryOptions(projectId));
  // Already cached by the drawings side and by this screen's own object pivot, so the
  // axis costs nothing extra.
  const objects = useProjectObjects(projectId);

  const facets = useMemo<readonly SlicerFacet[]>(() => {
    const options = (one: SurfaceField): readonly SlicerOption[] => {
      if (one.type === "node") {
        return hierarchy.data === undefined
          ? []
          : slicerOptions(hierarchy.data.nodes);
      }
      if ((one.values?.length ?? 0) > 0) {
        // The server's own vocabulary, in the server's own order. Sorting it here would put
        // *closed* between *done* and *in progress* and lose the ladder the enum encodes.
        return (one.values ?? []).map((value) => ({
          id: value,
          // The catalogs already name these — `status.inProgress`, `kind.issue`,
          // `priority.high` — because the filter bar renders the same words. Reading the same
          // key is what keeps the rail and the bar from calling one status two things.
          label: t(`${camel(one.field)}.${camel(value)}`, value),
        }));
      }
      if (one.field === "assignee_user_id") {
        return (members.data?.items ?? []).map((member) => ({
          id: member.user_id,
          label: member.display_name ?? member.email ?? member.user_id,
        }));
      }
      if (one.field === "label_ids") {
        return (labels.data?.items ?? []).map((label) => ({
          id: label.id,
          label: label.name,
        }));
      }
      if (one.field === "object_id") {
        // Archived objects are dropped: an archived SO is somewhere a drawing may still *be*
        // filed and nowhere new work is narrowed to. `label` and not `number`, because a bare
        // figure in a list of places reads as a count.
        return (objects.data ?? [])
          .filter((object) => object.archived_at === null)
          .map((object) => ({ id: object.id, label: object.label }));
      }
      // A `values_from` axis this screen has no query for. Offering it empty would be a rail
      // row that says "no locations" about people, so it is dropped instead — see `facets`.
      return [];
    };

    return offerableAxes(descriptor.data)
      .filter((one) => isBound(one.field))
      .map((one) => ({
        id: one.field,
        label: t(`slicer.${camel(one.field)}`, one.field),
        options: options(one),
        empty: t("slicer.noValues", ""),
        // Generic on purpose: `under.unknown` says "a part of the project", which is right
        // for the tree and wrong for a status. What the rail needs here is one sentence that
        // is true of any axis — a selection this rail cannot name.
        unknownLabel: t("slicer.unknownValue", ""),
      }))
      // An axis this screen has no values for is not drawn. An empty rail row invites a press
      // that does nothing, and the sentence under it would be about the wrong kind of thing.
      .filter((facet) => facet.options.length > 0);
  }, [descriptor.data, hierarchy.data, members.data, labels.data, objects.data, t]);

  return {
    facets,
    sliced: (facetId) => (isBound(facetId) ? BINDINGS[facetId].read(filters) : []),
    apply: applyTaskAxis,
  };
}
